import { z } from 'zod';

/**
 * Hex color validation regex
 */
const hexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color format');

/**
 * Zod schema for runtime validation of scraped filament data
 */
export const scrapedFilamentSchema = z.object({
  // Identifiers
  externalId: z.string().min(1).max(200),
  name: z.string().min(1).max(500),

  // Type info
  materialType: z.string().min(1).max(100),
  finish: z.string().max(100).optional(),
  embeddedMaterial: z.string().max(100).optional(),

  // Color
  colorName: z.string().min(1).max(200),
  colorHexes: z.array(hexColorSchema).min(1).max(10),

  // Physical
  diameter: z.union([z.literal(1.75), z.literal(2.85)]),
  spoolWeight: z.number().positive().max(50000), // max 50kg

  // Temps (0-500°C)
  hotendTempMin: z.number().int().min(0).max(500).optional(),
  hotendTempMax: z.number().int().min(0).max(500).optional(),
  bedTempMin: z.number().int().min(0).max(200).optional(),
  bedTempMax: z.number().int().min(0).max(200).optional(),

  // Drying
  dryingRequired: z.boolean().optional(),
  dryingTemp: z.number().int().min(0).max(150).optional(),
  dryingTime: z.number().int().min(0).max(72).optional(), // max 72 hours

  // Source
  productUrl: z.string().url(),
  lastScrapedAt: z.coerce.date(), // Coerce from ISO string to Date
});

/**
 * Raw scraped filament data before mapping to database schema
 */
export type ScrapedFilament = z.infer<typeof scrapedFilamentSchema>;

/**
 * Validate a scraped filament object
 * @throws ZodError if validation fails
 */
export function validateScrapedFilament(data: unknown): ScrapedFilament {
  return scrapedFilamentSchema.parse(data);
}

/**
 * Safe parse result type
 */
export type SafeParseResult = ReturnType<typeof scrapedFilamentSchema.safeParse>;

/**
 * Safely validate a scraped filament, returning result object
 */
export function safeValidateScrapedFilament(data: unknown): SafeParseResult {
  return scrapedFilamentSchema.safeParse(data);
}

/**
 * Interface all brand scrapers must implement
 */
export interface BrandScraper {
  /** Display name: 'Bambu Lab' */
  readonly brandName: string;
  /** URL slug: 'bambu-labs' */
  readonly brandSlug: string;
  /** Base URL for the brand's store */
  readonly baseUrl: string;

  /**
   * Scrape all filaments from the brand's website
   */
  scrapeAll(): Promise<ScrapedFilament[]>;

  /**
   * Scrape a single product by URL (for targeted updates)
   */
  scrapeProduct(url: string): Promise<ScrapedFilament | null>;
}

/**
 * JSON file format for storing filament data
 * One file per brand in apps/api/prisma/filaments/
 */
export interface FilamentJsonFile {
  /** Brand display name (e.g., "Bambu Lab") */
  brand: string;
  /** Brand slug for file naming (e.g., "bambu-labs") */
  brandSlug: string;
  /** ISO 8601 timestamp of when file was last generated */
  lastUpdated: string;
  /** Schema version for future compatibility */
  version: number;
  /** Array of scraped filament data */
  filaments: ScrapedFilament[];
}

/**
 * Zod schema for FilamentJsonFile validation
 */
export const filamentJsonFileSchema = z.object({
  brand: z.string().min(1),
  brandSlug: z.string().min(1).regex(/^[a-z0-9-]+$/),
  lastUpdated: z.string().datetime(),
  version: z.number().int().positive(),
  filaments: z.array(scrapedFilamentSchema),
});

/**
 * Validate a filament JSON file
 * @throws ZodError if validation fails
 */
export function validateFilamentJsonFile(data: unknown): FilamentJsonFile {
  return filamentJsonFileSchema.parse(data);
}

/**
 * Safely validate a filament JSON file, returning result object
 */
export function safeValidateFilamentJsonFile(
  data: unknown
): ReturnType<typeof filamentJsonFileSchema.safeParse> {
  return filamentJsonFileSchema.safeParse(data);
}
